import type {
    ConfigTarget,
    SettingsRegistryEntry,
    SettingsSnapshot,
} from "@magic-md/engine-client/config";
import { useT } from "@magic-md/i18n/react";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { cn } from "../../../lib/utils";
import { getPlatformService } from "../../../platform";
import { useSettingsStore } from "../../../store/settings-store";
import { getThemeAccentStyle } from "../../../theme/runtime/theme-accents";
import { countDetailFields, resolvePrimaryDetailEntry } from "./builders";
import { EFFECT_LABELS, SOURCE_LABELS } from "./constants";
import { formatDetailedValue } from "./format-utils";
import { SettingEditor } from "./InputEditors";
import {
    Badge,
    DetailModeSwitch,
    InfoChip,
    MiniBadge,
} from "./LayoutComponents";
import {
    settingsEntryDescription,
    settingsEntryTitle,
    useSettingsTextResolver,
} from "./localization";
import {
    effectActionLabel,
    getCurrentLayerValue,
    getExplicitLayerValue,
    isWritable,
    normalizeInputValue,
    overrideMessage,
    supportedTargets,
    targetLabel,
} from "./resolvers";
import { schemaPrimaryType } from "./schema-helpers";
import { ThemeSettingContextPanel } from "./ThemeSelectors";
import type { DetailMode, SettingGroup } from "./types";

export function GroupDetailPanel({
    group,
    target,
    snapshot,
    drafts,
    staleKeys,
    savingKeys,
    detailMode,
    onDetailModeChange,
    onDraftChange,
    onDiscardDraft,
    onCommit,
    onClear,
    onReload,
}: {
    group: SettingGroup;
    target: ConfigTarget;
    snapshot: SettingsSnapshot | null;
    drafts: Record<string, unknown>;
    staleKeys: string[];
    savingKeys: string[];
    detailMode: DetailMode;
    onDetailModeChange: (mode: DetailMode) => void;
    onDraftChange: (key: string, value: unknown) => void;
    onDiscardDraft: (key: string) => void;
    onCommit: (entry: SettingsRegistryEntry, value: unknown) => Promise<void>;
    onClear: (entry: SettingsRegistryEntry) => Promise<void>;
    onReload: () => Promise<void>;
}) {
    const t = useT("editor");
    const resolveText = useSettingsTextResolver();
    const enabledValue = group.enabledEntry
        ? getCurrentLayerValue(
            group.enabledEntry,
            target,
            snapshot,
            drafts[group.enabledEntry.key],
        ) !== false
        : true;
    const detailEntries = group.entries.filter(
        (entry) => entry.key !== group.enabledEntry?.key,
    );
    const primaryDetailEntry = resolvePrimaryDetailEntry(group, detailEntries);
    const primaryDetailMeta = primaryDetailEntry
        ? snapshot?.meta.entries[primaryDetailEntry.key]
        : undefined;
    const primaryDraftValue = primaryDetailEntry
        ? drafts[primaryDetailEntry.key]
        : undefined;
    const primaryWritable = primaryDetailEntry
        ? isWritable(
            primaryDetailEntry,
            target,
            snapshot?.capabilities,
            primaryDetailMeta,
        )
        : false;
    const primaryClearable =
        !!primaryDetailMeta?.setInTargets?.[target] &&
        primaryWritable;
    const primaryDraftType = primaryDetailEntry
        ? schemaPrimaryType(primaryDetailEntry.schema)
        : null;
    const primaryShowDraftActions = primaryDetailEntry
        ? primaryDraftValue !== undefined &&
        (primaryDraftType === "array" || primaryDraftType === "object")
        : false;
    const primaryEffect = primaryDetailMeta?.effect ?? primaryDetailEntry?.effect;
    const primaryEffectLabel = primaryEffect
        ? resolveText(EFFECT_LABELS[primaryEffect])
        : null;
    const primarySaving = primaryDetailEntry
        ? savingKeys.includes(primaryDetailEntry.key)
        : false;
    const detailFieldCount = countDetailFields(detailEntries);

    return (
        <div className="min-h-full">
            <div className="border-b border-border pb-2">
                <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                        <span className="mt-0.5 text-xl leading-none">{group.icon}</span>
                        <div className="min-w-0">
                            <div className="text-base text-foreground">
                                {resolveText(group.title, group.id)}
                            </div>
                            <div className="mt-1 text-[11px] font-mono text-muted-foreground">
                                {group.id}
                            </div>
                            {primaryDetailEntry ? (
                                <div className="mt-1 text-[11px] font-mono text-muted-foreground/80">
                                    {primaryDetailEntry.key}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                        <DetailModeSwitch mode={detailMode} onChange={onDetailModeChange} />
                        <Badge>
                            {enabledValue
                                ? t("settings.boolean.enabled")
                                : t("settings.boolean.stopped")}
                        </Badge>
                        {primaryEffectLabel ? (
                            <MiniBadge>{primaryEffectLabel}</MiniBadge>
                        ) : null}
                        {primaryDetailMeta?.readOnlyReason ? (
                            <MiniBadge>
                                {resolveText(primaryDetailMeta.readOnlyReason, primaryDetailMeta.readOnlyReason)}
                            </MiniBadge>
                        ) : null}
                        <MiniBadge>
                            {detailFieldCount > 0
                                ? t("settings.configItemCount", { count: detailFieldCount })
                                : t("settings.noExtraConfig")}
                        </MiniBadge>
                        {primaryClearable ? (
                            <button
                                type="button"
                                onClick={() => {
                                    if (!primaryDetailEntry) return;
                                    void onClear(primaryDetailEntry);
                                }}
                                className="rounded-md border border-border px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            >
                                {t("settings.clearTarget", {
                                    target: targetLabel(target, resolveText),
                                })}
                            </button>
                        ) : null}
                        {primaryShowDraftActions ? (
                            <>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!primaryDetailEntry) return;
                                        void onCommit(
                                            primaryDetailEntry,
                                            normalizeInputValue(
                                                primaryDetailEntry,
                                                primaryDraftValue,
                                            ),
                                        );
                                    }}
                                    className="rounded-md border border-primary/30 bg-primary/10 px-2.5 py-1 text-[11px] text-primary transition-colors hover:bg-primary/15"
                                >
                                    {t("settings.save")}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!primaryDetailEntry) return;
                                        onDiscardDraft(primaryDetailEntry.key);
                                    }}
                                    className="rounded-md border border-border px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                >
                                    {t("settings.cancel")}
                                </button>
                            </>
                        ) : null}
                        {primarySaving ? (
                            <Loader2
                                size={14}
                                className="shrink-0 animate-spin text-muted-foreground"
                            />
                        ) : null}
                    </div>
                </div>
                {group.description ? (
                    <div className="mt-2 pl-8 text-sm leading-6 text-muted-foreground">
                        {resolveText(group.description)}
                    </div>
                ) : null}
            </div>
            <div className="space-y-2.5 pt-2.5">
                {detailEntries.length === 0 ? (
                    <div className="rounded-md border border-border/80 bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
                        {t("settings.noLongTermConfig")}
                    </div>
                ) : null}
                {detailEntries.map((entry) => (
                    <SettingDetailPanel
                        key={entry.key}
                        entry={entry}
                        target={target}
                        snapshot={snapshot}
                        draftValue={drafts[entry.key]}
                        stale={staleKeys.includes(entry.key)}
                        saving={savingKeys.includes(entry.key)}
                        detailMode={detailMode}
                        onDetailModeChange={onDetailModeChange}
                        showDetailModeSwitch={false}
                        hideHeader={entry === primaryDetailEntry}
                        onDraftChange={(value) => onDraftChange(entry.key, value)}
                        onDiscardDraft={() => onDiscardDraft(entry.key)}
                        onCommit={(value) => onCommit(entry, value)}
                        onClear={() => onClear(entry)}
                        onReload={onReload}
                    />
                ))}
            </div>
        </div>
    );
}

export function SettingDetailPanel({
    entry,
    target,
    snapshot,
    draftValue,
    stale,
    saving,
    detailMode,
    onDetailModeChange,
    showDetailModeSwitch = true,
    hideHeader = false,
    onDraftChange,
    onDiscardDraft,
    onCommit,
    onClear,
    onReload,
}: {
    entry: SettingsRegistryEntry;
    target: ConfigTarget;
    snapshot: SettingsSnapshot | null;
    draftValue: unknown;
    stale: boolean;
    saving: boolean;
    detailMode: DetailMode;
    onDetailModeChange: (mode: DetailMode) => void;
    showDetailModeSwitch?: boolean;
    hideHeader?: boolean;
    onDraftChange: (value: unknown) => void;
    onDiscardDraft: () => void;
    onCommit: (value: unknown) => Promise<void>;
    onClear: () => Promise<void>;
    onReload: () => Promise<void>;
}) {
    const t = useT("editor");
    const resolveText = useSettingsTextResolver();
    const meta = snapshot?.meta.entries[entry.key];
    const currentLayerValue = getCurrentLayerValue(
        entry,
        target,
        snapshot,
        draftValue,
    );
    const finalValue = snapshot?.view.values[entry.key];
    const writable = isWritable(entry, target, snapshot?.capabilities, meta);
    const clearable = !!meta?.setInTargets?.[target] && writable;
    const overrideLabel = overrideMessage(target, meta, resolveText);
    const currentSourceLabel = meta
        ? resolveText(SOURCE_LABELS[meta.source])
        : resolveText(SOURCE_LABELS.default);
    const effectLabel = meta?.effect ? resolveText(EFFECT_LABELS[meta.effect]) : null;
    const description = settingsEntryDescription(entry, resolveText);
    const rawType = Array.isArray(entry.schema.type)
        ? entry.schema.type[0]
        : entry.schema.type;
    const showDraftActions =
        draftValue !== undefined && (rawType === "array" || rawType === "object");

    const commitParsedValue = async (rawValue: unknown) => {
        await onCommit(normalizeInputValue(entry, rawValue));
    };
    const infoVisible = detailMode === "info";

    return (
        <section className="border-b border-border/70 py-3 first:pt-0 last:border-b-0 last:pb-0">
            {!hideHeader ? (
                <>
                    <div className="flex flex-wrap items-start justify-between gap-2.5">
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-1.5">
                                <div className="text-sm text-foreground">
                                    {settingsEntryTitle(entry, resolveText)}
                                </div>
                                {!infoVisible && effectLabel ? (
                                    <MiniBadge>{effectLabel}</MiniBadge>
                                ) : null}
                                {!infoVisible && meta?.readOnlyReason ? (
                                    <MiniBadge>
                                        {resolveText(meta.readOnlyReason, meta.readOnlyReason)}
                                    </MiniBadge>
                                ) : null}
                            </div>
                            <div className="mt-0.5 text-[11px] font-mono text-muted-foreground">
                                {entry.key}
                            </div>
                        </div>
                        <div className="flex shrink-0 flex-wrap items-center gap-2">
                            {showDetailModeSwitch ? (
                                <DetailModeSwitch
                                    mode={detailMode}
                                    onChange={onDetailModeChange}
                                />
                            ) : null}
                            {clearable ? (
                                <button
                                    type="button"
                                    onClick={() => void onClear()}
                                    className="rounded-md border border-border px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                >
                                    {t("settings.clearTarget", {
                                        target: targetLabel(target, resolveText),
                                    })}
                                </button>
                            ) : null}
                            {showDraftActions ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => void commitParsedValue(draftValue)}
                                        className="rounded-md border border-primary/30 bg-primary/10 px-2.5 py-1 text-[11px] text-primary transition-colors hover:bg-primary/15"
                                    >
                                        {t("settings.save")}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onDiscardDraft}
                                        className="rounded-md border border-border px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                    >
                                        {t("settings.cancel")}
                                    </button>
                                </>
                            ) : null}
                            {saving ? (
                                <Loader2
                                    size={14}
                                    className="shrink-0 animate-spin text-muted-foreground"
                                />
                            ) : null}
                        </div>
                    </div>
                    {description ? (
                        <div className="mt-1.5 text-xs leading-5 text-muted-foreground">
                            {description}
                        </div>
                    ) : null}
                </>
            ) : null}

            <ThemeSettingContextPanel
                entry={entry}
                value={currentLayerValue ?? finalValue}
            />

            {infoVisible ? (
                <div className={cn("space-y-2", !hideHeader && "mt-2.5")}>
                    <div className="grid gap-2 md:grid-cols-2">
                        <InfoChip label={t("settings.info.source")} value={currentSourceLabel} />
                        {overrideLabel ? (
                            <InfoChip label={t("settings.info.override")} value={overrideLabel} />
                        ) : null}
                        {effectLabel ? (
                            <InfoChip label={t("settings.info.effect")} value={effectLabel} />
                        ) : null}
                        {meta?.readOnlyReason ? (
                            <InfoChip
                                label={t("settings.info.status")}
                                value={resolveText(meta.readOnlyReason, meta.readOnlyReason)}
                            />
                        ) : null}
                    </div>
                    <EffectNotice
                        entry={entry}
                        effect={meta?.effect}
                        value={currentLayerValue}
                        writable={writable}
                    />
                    <LayerResolutionGrid
                        entry={entry}
                        target={target}
                        snapshot={snapshot}
                        draftValue={draftValue}
                        finalValue={finalValue}
                    />
                </div>
            ) : (
                <div className={cn("space-y-2.5", !hideHeader && "mt-2.5")}>
                    {stale ? (
                        <div
                            className="theme-accent-banner rounded-md px-3 py-2 text-xs"
                            style={getThemeAccentStyle("warning")}
                        >
                            <div>{t("settings.stale.message")}</div>
                            <div className="mt-2 flex gap-2">
                                <button
                                    type="button"
                                    className="theme-accent-button rounded px-2 py-1 transition-colors"
                                    style={getThemeAccentStyle("warning")}
                                    onClick={() => {
                                        onDiscardDraft();
                                        void onReload();
                                    }}
                                >
                                    {t("settings.reload")}
                                </button>
                                <button
                                    type="button"
                                    className="theme-accent-button rounded px-2 py-1 transition-colors"
                                    style={getThemeAccentStyle("warning")}
                                    onClick={() => {
                                        // keep draft
                                    }}
                                >
                                    {t("settings.keepDraft")}
                                </button>
                            </div>
                        </div>
                    ) : null}

                    {entry.secret ? (
                        <div className="space-y-2">
                            {meta?.masked ? (
                                <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                                    {t("settings.secretMasked")}
                                </div>
                            ) : null}
                            <SettingEditor
                                entry={entry}
                                value={draftValue ?? defaultSecretDraftValue(entry)}
                                disabled={!writable}
                                onChange={onDraftChange}
                                onCommit={commitParsedValue}
                                onDiscard={onDiscardDraft}
                            />
                        </div>
                    ) : (
                        <SettingEditor
                            entry={entry}
                            value={currentLayerValue}
                            disabled={!writable}
                            onChange={onDraftChange}
                            onCommit={commitParsedValue}
                            onDiscard={onDiscardDraft}
                        />
                    )}
                    <EffectNotice
                        entry={entry}
                        effect={meta?.effect}
                        value={currentLayerValue}
                        writable={writable}
                    />
                </div>
            )}
        </section>
    );
}

function defaultSecretDraftValue(entry: SettingsRegistryEntry): unknown {
    const rawType = Array.isArray(entry.schema.type)
        ? entry.schema.type[0]
        : entry.schema.type;
    if (rawType === "object") return {};
    if (rawType === "array") return [];
    if (rawType === "boolean") return false;
    if (rawType === "number" || rawType === "integer") return "";
    return "";
}

function EffectNotice({
    entry,
    effect,
    value,
    writable,
}: {
    entry: SettingsRegistryEntry;
    effect?: SettingsSnapshot["meta"]["entries"][string]["effect"];
    value?: unknown;
    writable: boolean;
}) {
    const t = useT("editor");
    const resolveText = useSettingsTextResolver();
    const [applying, setApplying] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const supportedEffect =
        effect === "nextSession" || effect === "restart" ? effect : null;

    const canApply =
        writable &&
        !!supportedEffect &&
        (getPlatformService().canApplyConfigEffectNow?.({
            owner: entry.owner,
            key: entry.key,
            effect: supportedEffect,
        }) ??
            !!getPlatformService().applyConfigEffectNow);

    const handleApplyNow = async () => {
        const applyConfigEffectNow = getPlatformService().applyConfigEffectNow;
        if (
            !applyConfigEffectNow ||
            (effect !== "nextSession" && effect !== "restart")
        ) {
            return;
        }
        setApplying(true);
        setMessage(null);
        try {
            const result = await applyConfigEffectNow({
                owner: entry.owner,
                key: entry.key,
                effect,
                value,
            });
            setMessage(
                result.message ??
                (result.success
                    ? t("settings.apply.success")
                    : t("settings.apply.unavailable")),
            );
            if (result.success) {
                const { loadSchema, loadSnapshot } = useSettingsStore.getState();
                await Promise.all([loadSchema(), loadSnapshot()]);
            }
        } catch (error) {
            setMessage(error instanceof Error ? error.message : String(error));
        } finally {
            setApplying(false);
        }
    };

    if (effect === "nextSession") {
        return (
            <div
                className="rounded-md border px-3 py-2 text-[11px] theme-accent-banner"
                style={getThemeAccentStyle("info")}
            >
                <div className="flex flex-wrap items-center gap-2">
                    <span>{t("settings.effectNotice.nextSession")}</span>
                    {canApply ? (
                        <button
                            type="button"
                            onClick={() => void handleApplyNow()}
                            className="rounded border px-2 py-1 transition-colors theme-accent-button disabled:cursor-not-allowed disabled:opacity-60"
                            style={getThemeAccentStyle("info")}
                            disabled={applying}
                        >
                            {applying
                                ? t("settings.apply.applying")
                                : effectActionLabel(entry, resolveText)}
                        </button>
                    ) : null}
                    {message ? <span>{message}</span> : null}
                </div>
            </div>
        );
    }
    if (effect === "restart") {
        return (
            <div
                className="theme-accent-banner rounded-md px-3 py-2 text-[11px]"
                style={getThemeAccentStyle("warning")}
            >
                <div className="flex flex-wrap items-center gap-2">
                    <span>{t("settings.effectNotice.restart")}</span>
                    {canApply ? (
                        <button
                            type="button"
                            onClick={() => void handleApplyNow()}
                            className="theme-accent-button rounded px-2 py-1 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                            style={getThemeAccentStyle("warning")}
                            disabled={applying}
                        >
                            {applying
                                ? t("settings.apply.applying")
                                : effectActionLabel(entry, resolveText)}
                        </button>
                    ) : null}
                    {message ? <span>{message}</span> : null}
                </div>
            </div>
        );
    }
    return null;
}

function LayerResolutionGrid({
    entry,
    target,
    snapshot,
    draftValue,
    finalValue,
}: {
    entry: SettingsRegistryEntry;
    target: ConfigTarget;
    snapshot: SettingsSnapshot | null;
    draftValue: unknown;
    finalValue: unknown;
}) {
    const t = useT("editor");
    const resolveText = useSettingsTextResolver();
    const cells: Array<{
        id: ConfigTarget | "default" | "final";
        label: string;
        value: unknown;
        active: boolean;
        hasDraft: boolean;
        draftValue: unknown;
    }> = supportedTargets(entry).map((layerTarget) => ({
        id: layerTarget,
        label: targetLabel(layerTarget, resolveText),
        value: getExplicitLayerValue(entry, layerTarget, snapshot),
        active: layerTarget === target,
        hasDraft: layerTarget === target && draftValue !== undefined,
        draftValue: layerTarget === target ? draftValue : undefined,
    }));

    cells.push({
        id: "default",
        label: t("settings.layer.default"),
        value: entry.defaultValue,
        active: false,
        hasDraft: false,
        draftValue: undefined,
    });

    cells.push({
        id: "final",
        label: t("settings.layer.final"),
        value: finalValue,
        active: false,
        hasDraft: false,
        draftValue: undefined,
    });

    return (
        <div
            className={cn(
                "grid gap-2 rounded-md border border-border/80 bg-muted/10 px-3 py-2 text-[11px] text-muted-foreground",
                cells.length >= 5
                    ? "xl:grid-cols-5"
                    : cells.length === 4
                        ? "lg:grid-cols-4"
                        : cells.length === 3
                            ? "md:grid-cols-3"
                            : "md:grid-cols-2",
            )}
        >
            {cells.map((cell) => (
                <div
                    key={cell.label}
                    className={cn(
                        "min-w-0 rounded-md bg-background/70 px-2 py-2",
                        cell.active && "ring-1 ring-primary/20",
                    )}
                >
                    <div className="flex items-center gap-2">
                        <span>{cell.label}</span>
                        {cell.active ? <MiniBadge>{t("settings.layer.current")}</MiniBadge> : null}
                        {cell.hasDraft ? <MiniBadge>{t("settings.layer.draft")}</MiniBadge> : null}
                    </div>
                    <div className="mt-1 break-all whitespace-pre-wrap font-mono text-foreground/80">
                        {formatDetailedValue(
                            cell.hasDraft ? cell.draftValue : cell.value,
                            cell.id !== "final" && cell.id !== "default" && !cell.hasDraft,
                            resolveText,
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}
